import { flatMap } from 'es-toolkit';

import { Load, LoadCase } from '@paragon/contract-library';
import { LoadGroup } from '@paragon/layout-models';
import { enqueueInfo } from '@paragon/notification-tools';

import { ManualRenderModes } from '../../library/renderModes';
import { SelectableItemEntry, SelectableItemType } from '../../library/selection/types';
import { focusCameraOnSelectedObjects } from '../../three/ThreeJsSceneActions';
import { getActiveComponentDesign } from '../selectors/getActiveComponentDesignSelector';
import { getReferenceLineDisplayMode } from '../selectors/getReferenceLineDisplayMode';
import { getSingleSelectedItemEntryOrNull } from '../selectors/getSelectedItemSelector';
import { getActiveEnvelopeMode, getConfiguredEnvelopeMode } from '../selectors/layoutVisualOptionsSelectors';
import { getVisibleLoadCaseLoads } from '../selectors/loadVisibilitySelectors';
import {
  EnvelopeRenderMode,
  hiddenItemGuidsPush,
  hiddenItemGuidsRemove,
  hideLoads,
  setComponentForcesDisplayedForces,
  setManualRenderModeValue,
  setShearLineViewMode,
  setShowAnalogModel,
  setShowCheckResultsLoadCases,
  setShowStandardDimensions,
  setVisibleLoads,
  showLoads,
  toggleDimensions,
  toggleShowAnalogPanels,
  transparentGuidsRemove,
  transparentItemGuidsPush,
} from '../slices/visualOptionsSlice';
import { toggleSnappingUnderlay } from './layout/snappingUnderlayActions';
import { loadActiveAnalysisResults } from './loadAnalysisResults';
import { selectItem } from './selectActions';
import { ThunkActionVoid } from './thunkActions';

export const showAnalogModel =
  (visible = true): ThunkActionVoid =>
  (dispatch, getState) => {
    const state = getState();
    if (state.visualOptions.showAnalogModel === visible) return;
    if (!visible) {
      const selectedItem = getSingleSelectedItemEntryOrNull(state);
      if (selectedItem?.type === 'analogJoint' || selectedItem?.type === 'analogLink' || selectedItem?.type === 'panel')
        dispatch(selectItem(getActiveComponentDesign(state)!, 'componentDesign'));
    }
    dispatch(setShowAnalogModel(visible));
  };

export const toggleShowReferenceLines = (): ThunkActionVoid => (dispatch, getState) => {
  const referenceLineMode = getReferenceLineDisplayMode(getState());

  if (referenceLineMode.toggle) {
    dispatch(referenceLineMode.toggle());
  } else {
    dispatch(enqueueInfo("Reference lines can't be toggled at this time."));
  }
};

export const toggleShowStandardDimensions = (): ThunkActionVoid => (dispatch, getState) => {
  dispatch(setShowStandardDimensions(!getState().visualOptions.showStandardDimensions));
  focusCameraOnSelectedObjects();
};

export const toggleShowAnalogPanelsAndShowAnalogModel = (): ThunkActionVoid => (dispatch) => {
  dispatch(showAnalogModel());
  dispatch(toggleShowAnalogPanels());
};

export const cycleShearLineView = (): ThunkActionVoid => (dispatch, getState) => {
  const currentMode = getState().visualOptions.shearLineViewMode;
  const nextMode = currentMode === 'NONE' ? 'MERGED' : currentMode === 'MERGED' ? 'UNMERGED' : 'NONE';
  dispatch(setShearLineViewMode(nextMode));
};

export const setDisplayAnalogModel =
  (visible: boolean): ThunkActionVoid =>
  (dispatch, getState) => {
    const state = getState();
    if (!visible) {
      dispatch(setComponentForcesDisplayedForces([]));
      if (state.visualOptions.showAnalogPanels) {
        dispatch(toggleShowAnalogPanelsAndShowAnalogModel());
      }
    }
    dispatch(showAnalogModel(visible));
  };

export const toggleSingleDisplayedForces =
  (selectedForceDisplay: string): ThunkActionVoid =>
  (dispatch, getState) => {
    const state = getState();
    const displayedForces = [...state.visualOptions.displayedForces];
    if (!displayedForces.includes(selectedForceDisplay)) {
      dispatch(showAnalogModel());
      dispatch(loadActiveAnalysisResults());
      displayedForces.push(selectedForceDisplay);
    } else {
      displayedForces.splice(displayedForces.indexOf(selectedForceDisplay), 1);
    }
    dispatch(setComponentForcesDisplayedForces(displayedForces));
  };

export const ITEMS_WITH_DIMENSIONS: ReadonlyArray<SelectableItemType> = [
  'member',
  'brace',
  'platePair',
  'componentDesign',
];

export const toggleDimensionsForSelectedItem = () => toggleDimensions();

export const toggleCheckResultsLoadCasesVisibility =
  ({ loads }: LoadGroup | LoadCase): ThunkActionVoid =>
  (dispatch, getState) => {
    const state = getState();
    const showLoadCases = state.visualOptions.showCheckResultsLoadCases;
    const guids = loads.map((load) => load.guid);
    if (showLoadCases) {
      dispatch(hideLoads(guids));
      dispatch(setShowCheckResultsLoadCases(false));
    } else {
      dispatch(showLoadsAndHideOtherLoadCases(loads));
      dispatch(setShowCheckResultsLoadCases(true));
    }
  };

export const displaySelectedCheckResultLoadCase =
  (loadCase: LoadCase | null): ThunkActionVoid =>
  (dispatch, getState) => {
    if (!loadCase) {
      dispatch(setVisibleLoads([]));
    } else {
      const { loads } = loadCase;
      const state = getState();
      const guids = loads.map((load) => load.guid);
      const visible = !guids.every((guid) => !state.visualOptions.visibleLoadGuids[guid]);
      if (!visible) {
        dispatch(showLoadsAndHideOtherLoadCases(loads));
      }
    }
  };

export const cycleVisibilityOfItem =
  (entry: SelectableItemEntry | { type: 'loadGroup'; item: LoadGroup } | null): ThunkActionVoid =>
  (dispatch, getState) => {
    if (!entry) return;

    const state = getState();

    if (entry.type === 'snappingUnderlay') {
      dispatch(toggleSnappingUnderlay(entry.item.guid));
      return;
    }

    if (entry.type === 'loadCase' || entry.type === 'loadGroup' || entry.type === 'load') {
      const loads = entry.type === 'load' ? [entry.item] : entry.item.loads;
      const guids = loads.map((load) => load.guid);
      const visible = guids.some((guid) => guid in state.visualOptions.visibleLoadGuids);
      if (visible) {
        dispatch(hideLoads(guids));
        if (getVisibleLoadCaseLoads(getState()).length === 0) {
          const activeComponentDesign = getActiveComponentDesign(state);
          dispatch(selectItem(activeComponentDesign, 'componentDesign'));
        }
      } else {
        dispatch(showLoadsAndHideOtherLoadCases(loads));
      }
      return;
    }

    const hidden = state.visualOptions.hiddenObjectGuids.some((guid) => guid === entry.item.guid);
    const transparent = state.visualOptions.transparentObjectGuids.some((guid) => guid === entry.item.guid);
    switch (entry.type) {
      case 'analogLink':
      case 'analogJoint':
      case 'bearing':
      case 'referenceLine': {
        if (hidden) dispatch(hiddenItemGuidsRemove(entry.item.guid));
        else dispatch(hiddenItemGuidsPush(entry.item.guid));
        break;
      }

      case 'platePair':
      case 'member':
      case 'brace': {
        if (hidden) {
          dispatch(hiddenItemGuidsRemove(entry.item.guid));
          dispatch(transparentItemGuidsPush(entry.item.guid));
        } else if (transparent) {
          dispatch(transparentGuidsRemove(entry.item.guid));
        } else {
          dispatch(hiddenItemGuidsPush(entry.item.guid));
        }
        break;
      }

      // no default
    }
  };

const showLoadsAndHideOtherLoadCases =
  (loads: ReadonlyArray<Load>): ThunkActionVoid =>
  (dispatch, getState) => {
    const { loadSets } = getState();
    if (!loads.length) {
      return;
    }
    dispatch(
      hideLoads(
        flatMap(
          flatMap(Object.values(loadSets), (loadSet) => loadSet.loadCases).filter(
            (loadCase) => loadCase.guid !== loads[0].loadCaseGuid,
          ),
          (loadCase) => loadCase.loads,
        ).map((load) => load.guid),
      ),
    );
    dispatch(showLoads(loads.map((load) => load.guid)));
  };

export const cycleTrussEnvelopeRenderMode = (): ThunkActionVoid => (dispatch, getState) => {
  // FIXME: this implementation is rather hardcoded since there is not a programmatic way to fetch the possible manual
  //   values of a render mode. If implementing this shortcut for any other render mode, you should consider exploring
  //   ways to accomplish that, and using it for a generic "cycle modes" action as well as for the render mode buttons
  //   in the toolbar.

  const options = ['components', 'trussSolids', 'envelope'] satisfies ManualRenderModes<EnvelopeRenderMode>[];

  const state = getState();
  const configured = getConfiguredEnvelopeMode(state);
  const actual = getActiveEnvelopeMode(state);

  const configuredIndex = (options as string[]).indexOf(configured);
  const actualIndex = (options as string[]).indexOf(actual);

  const currentIndex = configuredIndex !== -1 ? configuredIndex : actualIndex !== -1 ? actualIndex : 0;
  const nextIndex = (currentIndex + 1 + options.length) % options.length;

  const nextMode = options[nextIndex];

  dispatch(setManualRenderModeValue({ type: 'envelopeMode', mode: nextMode }));
};
